import type React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import type { OfferedAnswerDto } from "@/api/types";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

import type { CommunityPollVoteFormValues } from "./CommunityPollVoteForm";

interface CommunityPollVoteAnswersInputProps {
  offeredAnswers: OfferedAnswerDto[];
  allowMultipleAnswers: boolean;
}

export function CommunityPollVoteAnswersInput({
  offeredAnswers,
  allowMultipleAnswers,
}: CommunityPollVoteAnswersInputProps): React.ReactNode {
  const form = useFormContext<CommunityPollVoteFormValues>();
  const { t } = useTranslation();

  return (
    <FormErrorWrapper name="votes" encircle>
      <Controller<CommunityPollVoteFormValues, "votes">
        name="votes"
        control={form.control}
        rules={{
          validate: {
            required: (value) => {
              if (allowMultipleAnswers && value.length === 0) {
                return t("components.form.error.at-least-one-selected");
              }

              if (!allowMultipleAnswers && value.length === 0) {
                return t("components.form.error.exactly-one-selected");
              }
            },
          },
        }}
        render={({ field }) => {
          return (
            <ul className="flex w-full flex-col gap-2" data-testid="community-poll-vote-input">
              {offeredAnswers.map((answer) => {
                const isSelected = field.value.includes(answer.id);

                return (
                  <li key={answer.id}>
                    <label
                      className={twResolve(
                        "relative flex cursor-pointer items-center gap-2 rounded-lg border-[0.5px] border-grey-300 p-4 transition-colors duration-100",
                        isSelected
                          ? "border-aop-dark-blue-500 bg-aop-dark-blue-500"
                          : "bg-white hover:bg-aop-basic-blue-100",
                      )}
                    >
                      <input
                        className="peer absolute z-10 size-4 opacity-0"
                        type={allowMultipleAnswers ? "checkbox" : "radio"}
                        id={answer.id}
                        checked={isSelected}
                        value={answer.id}
                        onChange={() => {
                          if (allowMultipleAnswers) {
                            field.onChange(
                              isSelected ? field.value.filter((id) => id !== answer.id) : [...field.value, answer.id],
                            );
                          } else {
                            field.onChange([answer.id]);
                          }
                        }}
                      />
                      <div className="flex size-4 cursor-pointer items-center justify-center rounded-full border border-grey-300 bg-white peer-checked:border-white peer-focus-visible:ring-1 peer-focus-visible:ring-offset-1">
                        {isSelected && <CheckIcon size={12} className="text-aop-dark-blue-500" />}
                      </div>
                      <span className={twJoin(isSelected ? "text-body-bold text-white" : "text-body text-black")}>
                        {answer.text}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          );
        }}
      />
    </FormErrorWrapper>
  );
}
